import {
  Modal as AriaModal,
  Dialog,
  DialogTrigger,
  OverlayTriggerStateContext,
} from "react-aria-components";
import type { ComponentProps } from "react";
import type { DialogProps, ModalOverlayProps } from "react-aria-components";
import React, { useContext } from "react";

import { cn } from "@/lib/helpers/cn";

import { CloseButton } from "../atoms/CloseButton";
import { Heading2 } from "../atoms/Heading";
import { ModalOverlay2 } from "../atoms/ModalOverlay";

type ModalProps = ModalBaseProps & {
  overlayProps?: ModalOverlayProps;
  trigger: React.ReactElement;
};
export function Modal2({ children, overlayProps, trigger, ...dialogProps }: ModalProps) {
  return (
    <DialogTrigger>
      {trigger}

      <ModalOverlay2 {...overlayProps}>
        <ModalBase {...dialogProps}>{children}</ModalBase>
      </ModalOverlay2>
    </DialogTrigger>
  );
}

type ControlledModalProps = ModalBaseProps & {
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  overlayProps?: Omit<ModalOverlayProps, "isOpen" | "onOpenChange">;
};
export function ModalControlled2({
  children,
  isOpen,
  onOpenChange,
  overlayProps,
  ...dialogProps
}: ControlledModalProps) {
  return (
    <ModalOverlay2 isOpen={isOpen} onOpenChange={onOpenChange} {...overlayProps}>
      <ModalBase {...dialogProps}>{children}</ModalBase>
    </ModalOverlay2>
  );
}

type ModalBaseProps = DialogProps & { title?: string };

function ModalBase({ children, className, title, ...dialogProps }: ModalBaseProps) {
  const state = useContext(OverlayTriggerStateContext);
  const resolvedChildren =
    typeof children === "function" ? children({ close: () => state?.close() }) : children;

  return (
    <AriaModal className={styles.modal}>
      <Dialog {...dialogProps} aria-label={title} className={cn(styles.dialog, className)}>
        {title && (
          <>
            <Heading2 className="trim-both tw:max-w-8/10" size={2} slot="title">
              {title}
            </Heading2>
            <ModalDivider className="tw:-mt-0.5!" />
          </>
        )}

        {resolvedChildren}

        <CloseButton className={styles.close} slot="close" />
      </Dialog>
    </AriaModal>
  );
}

const styles = {
  close: "tw:absolute tw:top-1 tw:right-1 tw:z-1",
  dialog: [
    "aria-dialog",
    "tw:relative tw:min-w-120",
    "tw:bg-fill-white tw:rounded-lg tw:p-6 tw:shadow-2xl",
    "tw:flex tw:flex-col tw:gap-6",
    "tw:outline-none",
  ],
  modal: "aria-modal tw:max-w-lg",
} as const;

export function ModalDivider({ className, ...props }: ComponentProps<"hr">) {
  return (
    <hr
      className={cn(
        "tw:h-px tw:w-[calc(100%+(--spacing(12)))]",
        "tw:-left-3 tw:my-0! tw:-translate-x-6",
        "tw:bg-stroke-quaternary",
        className,
      )}
      {...props}
    />
  );
}
